import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  createCheckout,
  CheckoutRequest,
  getSubscriptionStatus,
  SubscriptionStatusResponse,
} from '../../services/api/subscriptionService';
import { cancelSubscription } from '../../services/api/organizationManagementService';
import { useConfirm } from '../../components/tasky';
import PlanChangeModal from '../../components/organization/PlanChangeModal';
import { RootState } from '../../redux/store';
import tokenManager from '../../utils/tokenManager';
import { track, ANALYTICS_EVENTS } from '../../utils/productAnalytics';
import { behavior } from '../../analytics';

interface PlanSpec {
  label: string;
  value: string;
  highlight?: boolean;
}

interface Plan {
  id: CheckoutRequest['plan'];
  name: string;
  target: string;
  price: string;
  priceOriginal: string;
  recommended: boolean;
  specs: PlanSpec[];
}

const plans: Plan[] = [
  {
    id: 'personal',
    name: 'PERSONAL',
    target: '個人・1人マーケターに',
    price: '9,800',
    priceOriginal: '16,000',
    recommended: false,
    specs: [
      { label: 'クレジット', value: '10,000' },
      { label: 'ユーザー数', value: '1 ID' },
      { label: '商品登録数', value: '3' },
      { label: '月間生成目安', value: '約180枚' },
      { label: '1枚あたり', value: '約¥54' },
    ],
  },
  {
    id: 'team',
    name: 'TEAM',
    target: 'バナー内製化を進めたいチームに',
    price: '30,000',
    priceOriginal: '50,000',
    recommended: true,
    specs: [
      { label: 'クレジット', value: '35,000' },
      { label: 'ユーザー数', value: '無制限', highlight: true },
      { label: '商品登録数', value: '3' },
      { label: '月間生成目安', value: '約650枚', highlight: true },
      { label: '1枚あたり', value: '約¥46', highlight: true },
    ],
  },
  {
    id: 'agency',
    name: 'AGENCY',
    target: '複数クライアントを担当する代理店に',
    price: '50,000',
    priceOriginal: '80,000',
    recommended: false,
    specs: [
      { label: 'クレジット', value: '60,000' },
      { label: 'ユーザー数', value: '無制限', highlight: true },
      { label: '商品登録数', value: '無制限', highlight: true },
      { label: '月間生成目安', value: '約1,100枚' },
      { label: '1枚あたり', value: '約¥45' },
    ],
  },
];

const TRIAL_DAYS = 7;

const PlansPage: React.FC = () => {
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isFromSignup = searchParams.get('from') === 'signup';
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [cancelingPlan, setCancelingPlan] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatusResponse | null>(null);
  // 楽観的更新: キャンセル成功直後にローカルで「解約予約済み」を覚えておく。
  // backend の has_subscription は Stripe webhook 経由でしか false に切り替わら
  // ないため、これがないと連打でキャンセル API を二度叩けてしまう (UX 上紛らわ
  // しい)。リロードで状態は消えるが、それでも直後の二度押しは確実に防げる。
  const [canceledPlans, setCanceledPlans] = useState<Set<string>>(new Set());
  // 契約中の組織がプランを変更するためのモーダル。
  // 契約中に別プランの申し込みボタンを押すと backend が 409（二重課金ガード）を返すので、
  // その導線をここへ振り替える。409 自体は正しいガードなので消さない。
  const [planChangeTarget, setPlanChangeTarget] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{
    type: 'success' | 'canceled' | 'error';
    text: string;
  } | null>(null);

  // 認証済みリーダーのみ「キャンセル」ボタンを出す。
  // 一般メンバーには backend (checkLeaderPermission) で 403 になる破壊的 CTA を
  // そもそも表示しない。
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isLeader = currentUser?.role === 'leader';

  // 現在のサブスクリプション状態を取得して、契約中プランのボタンを
  // 「キャンセル」表示に切り替えるために使う。
  const loadSubscriptionStatus = useCallback(async () => {
    // /plans は公開ページのため未認証ユーザーが訪れる。subscriptions/status は
    // auth:sanctum 配下なので、token 無しで呼ぶと axios interceptor が refresh
    // 失敗 → /login にハードリダイレクトしてしまい、価格ページが見られない
    // 状態になる。token がある場合のみ呼び出す。
    if (!tokenManager.getToken()) return;
    try {
      const status = await getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (err) {
      // 認証はあるが取得失敗 (例: ネットワーク不調)。画面自体は壊さない。
      console.warn('Failed to load subscription status', err);
    }
  }, []);

  useEffect(() => {
    loadSubscriptionStatus();
  }, [loadSubscriptionStatus]);

  useEffect(() => {
    track(ANALYTICS_EVENTS.PRICING_VIEWED);
  }, []);

  // Stripe Checkout の cancel_url (`/plans?canceled=true`) から戻ってきた場合に
  // キャンセルメッセージを表示する。
  // 決済成功時は success_url が `/home?subscription=success` (backend の
  // SubscriptionCheckoutController) を指しており /plans?success=true には誰も
  // 戻ってこないため、旧「?success=true 検知 → claim → 紙吹雪」ロジックは
  // デッドコードだった。プランボーナスの claim と紙吹雪は HomePage 側の
  // claim useEffect に一本化済み。
  useEffect(() => {
    if (searchParams.get('canceled') === 'true') {
      setStatusMessage({
        type: 'canceled',
        text: 'お支払いがキャンセルされました。プランを選択して再度お試しください。',
      });
    }
  }, [searchParams]);

  // 該当プランが「現在契約中で有効」(active or trialing) かどうか。
  // backend `subscriptions/status` は has_subscription を
  // in_array(['active','trialing']) で判定済みなので、ここはそれを信頼する。
  // 権限は問わない (権限による「キャンセル可否」は canCancel で別途判定)。
  const isCurrentPlan = (planId: CheckoutRequest['plan']): boolean =>
    Boolean(
      subscriptionStatus?.has_subscription &&
        subscriptionStatus.plan === planId,
    );

  // キャンセル操作が可能かどうか (= 契約中プラン且つ leader)。
  // backend `cancelSubscription` は checkLeaderPermission で非リーダーを 403 に
  // するため、UI 上もリーダーのみキャンセルボタンを出す。
  const canCancel = (planId: CheckoutRequest['plan']): boolean =>
    isCurrentPlan(planId) && isLeader;

  // 楽観的更新で「解約予約済み」と判定するためのヘルパ。
  const isPlanCanceled = (planId: CheckoutRequest['plan']): boolean =>
    canceledPlans.has(planId);

  // 非リーダーかつ既契約組織のメンバーは、契約中プラン以外のカードでも
  // 重複 checkout を開始できる (backend の subscriptions/checkout は既存契約も
  // リーダー権限も検査しない) 抜け穴を防ぐ。一律 disabled にしてリーダーへの
  // 依頼動線にする。
  const hasOrgSubscription = Boolean(subscriptionStatus?.has_subscription);
  const blockedAsNonLeader = hasOrgSubscription && !isLeader;

  const handleSelectPlan = async (planId: CheckoutRequest['plan']) => {
    setLoadingPlan(planId);
    setStatusMessage(null);

    try {
      const data: CheckoutRequest = {
        plan: planId,
        billing_cycle: 'yearly',
      };
      const result = await createCheckout(data);
      // 行動分析: プラン購入ファネルの「決済開始」。外部（Stripe）へ出る前に送り切る
      behavior.action('checkout_started', { plan: planId });
      await behavior.flush();
      window.location.href = result.checkout_url;
    } catch (error: any) {
      setStatusMessage({
        type: 'error',
        text: error?.message || 'チェックアウトの作成に失敗しました。もう一度お試しください。',
      });
      setLoadingPlan(null);
    }
  };

  const handleCancelPlan = async (planId: CheckoutRequest['plan'], planName: string) => {
    const confirmed = await confirm({
      title: `${planName} プランを解約してよろしいですか？`,
      description: '現在の請求期間の終了日まではご利用いただけます。期間終了後、自動更新が停止されます。',
      tone: 'destructive',
      confirmText: '解約する',
    });
    if (!confirmed) return;

    setCancelingPlan(planId);
    setStatusMessage(null);

    try {
      await cancelSubscription();
      // 楽観的更新: backend の has_subscription は Stripe webhook 経由で
      // しか false に切り替わらないので、ローカルで「解約予約済み」を即記録して
      // ボタンを disabled 表示にする (連打による二重キャンセル防止)。
      setCanceledPlans((prev) => {
        const next = new Set(prev);
        next.add(planId);
        return next;
      });
      // 状態自体も一応再取得 (期間終了後 status が canceled に変わった瞬間に反映できる)。
      await loadSubscriptionStatus();
      setStatusMessage({
        type: 'success',
        text: '解約手続きが完了しました。現在の請求期間の終了日までは引き続きご利用いただけます。',
      });
    } catch (error: any) {
      setStatusMessage({
        type: 'error',
        text: error?.message || '解約処理に失敗しました。時間を置いて再度お試しください。',
      });
    } finally {
      setCancelingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-warm-bg overflow-x-hidden">
      <div className="w-full max-w-[1100px] mx-auto px-4 sm:px-6 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <Link
            to="/"
            className="inline-flex items-center text-[14px] text-dark/60 hover:text-dark transition-colors mb-8 no-underline"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            ホームに戻る
          </Link>

          <h1 className="text-[28px] md:text-[40px] font-bold tracking-[-0.02em] text-dark">
            プランを選んで、今すぐ始めましょう
          </h1>
          <p className="mt-3 text-[16px] text-dark/60 max-w-lg mx-auto">
            すべてのプランに{TRIAL_DAYS}日間の無料トライアルがついています。
          </p>

          {statusMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-[15px] font-medium ${
                statusMessage.type === 'success'
                  ? 'bg-success/10 text-success border border-success/20'
                  : statusMessage.type === 'canceled'
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {statusMessage.type === 'success' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {statusMessage.text}
              {statusMessage.type === 'success' && (
                <Link
                  to="/home"
                  className="ml-2 underline font-semibold hover:opacity-80"
                  style={{ textDecoration: 'underline' }}
                >
                  ダッシュボードへ
                </Link>
              )}
            </motion.div>
          )}
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className={`relative flex flex-col p-6 rounded-2xl border bg-white ${
                plan.recommended
                  ? 'border-success border-2 shadow-[0_4px_24px_rgba(62,178,152,0.12)]'
                  : 'border-warm-border'
              }`}
            >
              {plan.recommended && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center h-6 px-3 rounded-full bg-success text-white text-[12px] font-semibold">
                  人気
                </span>
              )}

              <h3 className="text-[18px] font-bold text-dark tracking-[0.04em]">
                {plan.name}
              </h3>
              <p className="mt-1 text-[13px] text-dark/40 leading-snug">
                {plan.target}
              </p>

              <p className="mt-4 text-[13px] text-dark/40 line-through">
                ¥{plan.priceOriginal}
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-[36px] font-extrabold text-dark tracking-tight">
                  ¥{plan.price}
                </span>
                <span className="text-[15px] text-dark/40">/月</span>
              </div>
              <span className="mt-1 inline-flex self-start items-center h-6 px-3 rounded bg-dark text-white text-[11px] font-semibold">
                β版特別価格
              </span>

              <div className="mt-6 space-y-3">
                {plan.specs.map((spec) => (
                  <div key={spec.label} className="flex items-center justify-between">
                    <span className="text-[14px] text-dark/60">{spec.label}</span>
                    <span
                      className={`text-[14px] font-semibold ${
                        spec.highlight ? 'text-success' : 'text-dark'
                      }`}
                    >
                      {spec.value}
                    </span>
                  </div>
                ))}
              </div>

              {isCurrentPlan(plan.id) && isPlanCanceled(plan.id) ? (
                // 楽観的更新で「解約予約済み」状態 (cancel API 成功直後)。
                // backend が webhook 経由で status='canceled' に切り替わるまでの
                // 間、ユーザーには「解約予約済み」を見せて二度押しを防ぐ。
                <button
                  disabled
                  className="mt-8 w-full h-12 rounded-full text-[15px] font-semibold bg-dark/10 text-dark/60 cursor-not-allowed"
                >
                  解約予約済み
                </button>
              ) : canCancel(plan.id) ? (
                // 現在契約中 + リーダー権限: キャンセルボタン
                <button
                  onClick={() => handleCancelPlan(plan.id, plan.name)}
                  disabled={cancelingPlan !== null || loadingPlan !== null}
                  className="mt-8 w-full h-12 rounded-full text-[15px] font-semibold transition-opacity hover:opacity-90 bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelingPlan === plan.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      処理中...
                    </span>
                  ) : (
                    'キャンセル'
                  )}
                </button>
              ) : isCurrentPlan(plan.id) ? (
                // 現在契約中だが leader 権限なし (一般メンバー): 表示のみ。
                // 「無料で試す」を見せると同じ組織で重複 Stripe checkout を開始
                // できてしまう (backend の subscriptions/checkout は既存契約を
                // 検査していない) ため、必ず disabled の「ご契約中」を表示する。
                <button
                  disabled
                  className="mt-8 w-full h-12 rounded-full text-[15px] font-semibold bg-dark/10 text-dark/60 cursor-not-allowed"
                  title="現在ご契約中のプランです。解約はリーダーにご依頼ください。"
                >
                  ご契約中
                </button>
              ) : blockedAsNonLeader ? (
                // 非リーダーかつ組織が既に別プラン契約中: 重複 checkout 防止
                // のため、契約中プラン以外も disabled にする。リーダー権限が
                // ないとプラン変更も不可。
                <button
                  disabled
                  className="mt-8 w-full h-12 rounded-full text-[15px] font-semibold bg-dark/10 text-dark/60 cursor-not-allowed"
                  title="プランの変更はリーダーにご依頼ください。"
                >
                  リーダーへ依頼
                </button>
              ) : hasOrgSubscription ? (
                // 契約中の組織（リーダー）が別プランを選んだ場合。
                // ここで createCheckout を叩くと必ず 409「既に有効なサブスクリプションが
                // あります」になるので、プラン変更モーダル（/v1/subscription/upgrade）へ回す。
                <button
                  onClick={() => setPlanChangeTarget(plan.id)}
                  disabled={loadingPlan !== null || cancelingPlan !== null}
                  className="mt-8 w-full h-12 rounded-full text-[15px] font-semibold transition-opacity hover:opacity-90 bg-dark text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  このプランに変更
                </button>
              ) : (
                // 未契約: 申し込みボタン
                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loadingPlan !== null || cancelingPlan !== null}
                  className="mt-8 w-full h-12 rounded-full text-[15px] font-semibold transition-opacity hover:opacity-90 bg-success text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingPlan === plan.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      処理中...
                    </span>
                  ) : (
                    '無料で試す →'
                  )}
                </button>
              )}
            </motion.div>
          ))}
        </div>

        {isFromSignup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 text-center"
          >
            <button
              type="button"
              onClick={() => navigate('/home')}
              className="text-[14px] text-dark/60 hover:text-dark transition-colors underline underline-offset-2"
            >
              スキップして始める
            </button>
          </motion.div>
        )}
      </div>

      {planChangeTarget && (
        <PlanChangeModal
          currentPlan={subscriptionStatus?.plan ?? ''}
          initialPlanSlug={planChangeTarget}
          onClose={() => setPlanChangeTarget(null)}
          onSuccess={() => {
            setPlanChangeTarget(null);
            setStatusMessage({ type: 'success', text: 'プランを変更しました。' });
            loadSubscriptionStatus();
          }}
        />
      )}
    </div>
  );
};

export default PlansPage;
